/**
 * Kabinetga E-imzo (ERI kaliti) bilan kirish.
 *
 * Oqim: E-IMZO dasturi topiladi → kalitlar ro'yxati → foydalanuvchi kalitni tanlaydi →
 * server bir martalik matn (challenge) beradi → matn E-IMZO dasturida imzolanadi (kalit
 * paroli faqat o'sha dasturda kiritiladi) → server imzoni E-IMZO tekshiruv xizmati orqali
 * tekshiradi va sertifikatdagi ЖШШИР bo'yicha hisobni topadi. Maxfiy kalit brauzer va
 * serverga chiqmaydi. Sinov rejimi yo'q: faqat haqiqiy ERI kaliti qabul qilinadi.
 */
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { homeRoute, useAuth } from "@/app/AuthContext";
import { ApiError } from "@/shared/api/client";
import { authApi } from "@/shared/api/endpoints";
import { useI18n } from "@/shared/i18n";
import { eriAvailable, listKeys, signWithKey } from "@/shared/lib/eimzo";
import type { EriKey } from "@/shared/lib/eimzo";
import { EImzoMark, Icon } from "@/shared/ui/Icon";

type Agent = "checking" | "missing" | "empty" | "ready";

export function EriLogin({ next }: { next: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  /** Server imzo bilan kirishga tayyormi (E-IMZO tekshiruv xizmati ulangan). `null` — hali bilinmaydi. */
  const [available, setAvailable] = useState<boolean | null>(null);
  const [agent, setAgent] = useState<Agent>("checking");
  const [keys, setKeys] = useState<EriKey[]>([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState<"" | "sign" | "login">("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    authApi
      .eimzoStatus()
      .then((data) => {
        if (!cancelled) setAvailable(data.available);
      })
      .catch(() => {
        if (!cancelled) {
          setAvailable(true); // holat noma'lum — baribir urinib ko'riladi, xato server javobida chiqadi
          setError(t("auth.err.network"));
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scan = useCallback(async () => {
    setAgent("checking");
    setError("");
    if (!(await eriAvailable())) {
      setAgent("missing");
      return;
    }
    try {
      const found = await listKeys();
      setKeys(found);
      setSelected(found[0]?.id ?? "");
      setAgent(found.length ? "ready" : "empty");
    } catch {
      setKeys([]);
      setAgent("empty");
    }
  }, []);

  useEffect(() => {
    if (available) void scan();
  }, [available, scan]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const key = keys.find((item) => item.id === selected);
    if (!key) return;

    setError("");
    setBusy("sign");
    try {
      const { challenge } = await authApi.eimzoChallenge();
      const pkcs7 = await signWithKey(key, challenge);
      setBusy("login");
      const result = await authApi.eimzoLogin({ pkcs7, challenge });
      setSession(result.user);
      navigate(next || homeRoute(result.user), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.isOffline ? t("auth.err.network") : caught.message);
      } else {
        setError(caught instanceof Error && caught.message ? caught.message : t("eri.err.generic"));
      }
      setBusy("");
    }
  }

  const working = busy !== "";

  return (
    <form className="oneid-card eri-card" onSubmit={submit} noValidate>
      <div className="oneid-head">
        <span className="oneid-logo eri-logo">
          <EImzoMark size={34} />
        </span>
        <div>
          <b>{t("eri.title")}</b>
          <span>{t("eri.sub")}</span>
        </div>
      </div>

      {error ? (
        <div className="alert alert-error" role="alert">
          <Icon name="alert" size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      {available === false ? (
        <div className="alert alert-info">
          <Icon name="info" size={18} />
          <span>{t("eri.unavailable")}</span>
        </div>
      ) : null}

      {available ? (
        <>
          {agent === "checking" ? <p className="muted">{t("eri.checking")}</p> : null}

          {agent === "missing" ? (
            <div className="eri-empty">
              <Icon name="alert" size={20} />
              <div>
                <b>{t("eri.missing.title")}</b>
                <p>{t("eri.missing.text")}</p>
              </div>
            </div>
          ) : null}

          {agent === "empty" ? (
            <div className="eri-empty">
              <Icon name="key" size={20} />
              <p>{t("eri.noKeys")}</p>
            </div>
          ) : null}

          {agent === "ready" ? (
            <fieldset className="eri-keys">
              <legend className="label">{t("eri.pick")}</legend>
              {keys.map((item) => (
                <label key={item.id} className={`eri-key${selected === item.id ? " is-selected" : ""}`}>
                  <input
                    type="radio"
                    name="eri-key"
                    checked={selected === item.id}
                    onChange={() => setSelected(item.id)}
                  />
                  <span className="eri-key-icon">
                    <Icon name="key" size={18} />
                  </span>
                  <span className="eri-key-body">
                    <b>{item.title}</b>
                    {item.subtitle ? <small>{item.subtitle}</small> : null}
                    <small>
                      {item.pinflTail ? `JShShIR ••••${item.pinflTail}` : item.serial}
                      {item.validTo ? ` · ${t("eri.validTo")} ${item.validTo}` : ""}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}

          <div className="eri-actions">
            {agent === "ready" ? (
              <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={working || !selected}>
                {busy === "sign" ? t("eri.signing") : busy === "login" ? t("auth.submitting") : t("eri.submit")}
                {working ? null : <Icon name="arrowRight" size={18} />}
              </button>
            ) : null}
            {agent !== "checking" ? (
              <button
                type="button"
                className="btn btn-outline btn-block"
                onClick={() => void scan()}
                disabled={working}
              >
                <Icon name="refresh" size={16} />
                {t("eri.rescan")}
              </button>
            ) : null}
          </div>

          <p className="hint">
            <Icon name="shield" size={12} /> {t("eri.privacy")}
          </p>
        </>
      ) : null}
    </form>
  );
}
